import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase } from './supabaseClient';
import './Flights.css';

const PrivateJets = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [privateJets, setPrivateJets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchPrivateJets();
  }, []);

  const fetchPrivateJets = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('private_jets')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setPrivateJets(data || []);
    } catch (error) {
      console.error('Error fetching private jets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleAddPrivateJet = () => {
    navigate('/admin/private-jets/add');
  };

  const handleViewPrivateJet = (jetId) => {
    navigate(`/admin/private-jets/${jetId}`);
  };

  const handleEditPrivateJet = (jetId) => {
    navigate(`/admin/private-jets/edit/${jetId}`);
  };

  const handleDeletePrivateJet = async (jetId) => {
    if (window.confirm('Are you sure you want to delete this private jet?')) {
      try {
        const { error } = await supabase
          .from('private_jets')
          .delete()
          .eq('id', jetId);
        
        if (error) throw error;
        fetchPrivateJets();
      } catch (error) {
        console.error('Error deleting private jet:', error);
        alert('Failed to delete private jet. Please try again.');
      }
    }
  };

  const handleToggleStatus = async (jetId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const { error } = await supabase
        .from('private_jets')
        .update({ status: newStatus })
        .eq('id', jetId);
      
      if (error) throw error;
      fetchPrivateJets();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  // Filter jets
  const filteredJets = privateJets.filter(jet => {
    const matchesSearch = 
      jet.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jet.aircraft_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jet.from_location?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      jet.to_location?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || jet.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredJets.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedJets = filteredJets.slice(startIndex, endIndex);

  return (
    <AdminLayout pageTitle="Private Jets">
      <div className="admin-flights-page">
        {/* Page Title */}
        <div className="admin-flights-header">
          <h1 className="admin-flights-title">Private Jets</h1>
          <p className="admin-flights-subtitle">Manage private jet listings</p>
        </div>

        {/* Top Action Bar */}
        <div className="admin-flights-controls">
          <div className="admin-controls-left">
            <button
              className="admin-add-flight-button"
              onClick={handleAddPrivateJet}
            >
              + Add Private Jet
            </button>
            
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by name, aircraft type, or location..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <select
              className="admin-filter-select"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="admin-loading">Loading private jets...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Aircraft Type</th>
                  <th>From → To</th>
                  <th>Price</th>
                  <th>Capacity</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedJets.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="admin-table-empty">
                      No private jets found
                    </td>
                  </tr>
                ) : (
                  paginatedJets.map((jet) => (
                    <tr key={jet.id}>
                      <td>
                        <div className="admin-table-cell-primary">{jet.name || 'N/A'}</div>
                      </td>
                      <td>{jet.aircraft_type || 'N/A'}</td>
                      <td>
                        {jet.from_location || 'N/A'} → {jet.to_location || 'N/A'}
                      </td>
                      <td>
                        {jet.price ? `₦${parseFloat(jet.price).toLocaleString()}` : 'N/A'}
                      </td>
                      <td>{jet.capacity || 'N/A'} passengers</td>
                      <td>
                        <span className={`admin-status-badge admin-status-${jet.status || 'inactive'}`}>
                          {jet.status || 'inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="admin-table-actions">
                          <button
                            className="admin-action-button admin-action-view"
                            onClick={() => handleViewPrivateJet(jet.id)}
                            title="View"
                          >
                            👁️
                          </button>
                          <button
                            className="admin-action-button admin-action-edit"
                            onClick={() => handleEditPrivateJet(jet.id)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="admin-action-button admin-action-toggle"
                            onClick={() => handleToggleStatus(jet.id, jet.status)}
                            title={jet.status === 'active' ? 'Deactivate' : 'Activate'}
                          >
                            {jet.status === 'active' ? '⏸️' : '▶️'}
                          </button>
                          <button
                            className="admin-action-button admin-action-delete"
                            onClick={() => handleDeletePrivateJet(jet.id)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination-button"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <span className="admin-pagination-info">
              Page {currentPage} of {totalPages} ({filteredJets.length} total)
            </span>
            <button
              className="admin-pagination-button"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default PrivateJets;
